import { type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  LOADER_PRODUCTION_KIND_LABELS,
  LOADER_REMOVAL_REASON_LABELS,
  LOADER_STATUS_LABELS,
  type LoaderVendorQualityDto,
  type ProblemLoaderDto,
} from '@bhops/shared';
import { loadersApi } from '@/api/loaders';
import { extractApiError } from '@/api/client';
import { Icon } from '@/components/Icon';

// 로더 품질 분석 (읽기 전용) — 라우트 /manufacturing/loaders/quality.
//   업체별(외주 H5 vs 사내) 불량 교체율 + 자주 교체된 문제 로더 목록.

export function LoaderQualityPage() {
  const q = useQuery({
    queryKey: ['loader-quality'],
    queryFn: () => loadersApi.quality(),
    staleTime: 30_000,
  });

  return (
    <div style={pageStyle}>
      <div>
        <Link to="/manufacturing/loaders" style={backLink}>
          <Icon name="arrow-left" size={14} aria-hidden />
          로더 목록으로
        </Link>
        <h1 style={titleStyle}>로더 품질 · 문제 로더</h1>
      </div>

      {q.isLoading ? (
        <div style={loadingStyle}>불러오는 중…</div>
      ) : q.error ? (
        <div style={errorStyle}>{extractApiError(q.error)}</div>
      ) : (
        <>
          <VendorQualitySection rows={q.data?.byVendor ?? []} />
          <ProblemLoaderSection rows={q.data?.problemLoaders ?? []} />
        </>
      )}
    </div>
  );
}

function VendorQualitySection({ rows }: { rows: LoaderVendorQualityDto[] }) {
  return (
    <section style={card}>
      <h2 style={sectionHeaderStyle}>업체별 품질</h2>
      {rows.length === 0 ? (
        <div style={emptyBlock}>집계된 데이터가 없습니다.</div>
      ) : (
        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thLeft}>구분 · 업체</th>
                <th style={thRight}>로더 수</th>
                <th style={thRight}>불량 교체</th>
                <th style={{ ...thLeft, width: '40%' }}>불량률</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v, i) => {
                const pct = Math.round(v.defectRate * 100);
                const barColor = v.defectRate >= 0.3 ? 'var(--severity-urgent)' : 'var(--brand)';
                return (
                  <tr key={`${v.productionKind}-${v.vendor ?? ''}-${i}`}>
                    <td style={tdLeft}>
                      <span style={{ fontWeight: 600 }}>
                        {LOADER_PRODUCTION_KIND_LABELS[v.productionKind]}
                      </span>
                      {v.vendor ? (
                        <span style={{ color: 'var(--fg-muted)' }}> · {v.vendor}</span>
                      ) : null}
                    </td>
                    <td style={tdNum}>{v.totalLoaders}</td>
                    <td style={tdNum}>{v.removedForDefectCount}</td>
                    <td style={tdLeft}>
                      <div style={barRow}>
                        <div style={barTrack}>
                          <div
                            style={{
                              ...barFill,
                              width: `${Math.min(100, pct)}%`,
                              background: barColor,
                            }}
                          />
                        </div>
                        <span style={{ ...pctText, fontVariantNumeric: 'tabular-nums' }}>
                          {pct}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ProblemLoaderSection({ rows }: { rows: ProblemLoaderDto[] }) {
  return (
    <section style={card}>
      <h2 style={sectionHeaderStyle}>자주 교체되는 문제 로더</h2>
      {rows.length === 0 ? (
        <div style={emptyBlock}>자주 교체된 로더가 없습니다.</div>
      ) : (
        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thLeft}>시리얼</th>
                <th style={thRight}>교체 횟수</th>
                <th style={thLeft}>마지막 사유</th>
                <th style={thLeft}>상태</th>
                <th style={thLeft}>현재 로봇</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.loaderUnitId}>
                  <td style={tdLeft}>
                    <Link
                      to={`/manufacturing/loaders/${p.loaderUnitId}`}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 600,
                        color: 'var(--fg-brand)',
                      }}
                    >
                      {p.serial}
                    </Link>
                  </td>
                  <td style={tdNum}>{p.swapCount}</td>
                  <td style={tdLeft}>
                    {p.lastRemovalReason ? LOADER_REMOVAL_REASON_LABELS[p.lastRemovalReason] : '–'}
                  </td>
                  <td style={tdLeft}>{LOADER_STATUS_LABELS[p.status]}</td>
                  <td style={tdLeft}>
                    {p.currentRobotSerial ? (
                      <span style={{ fontFamily: 'var(--font-mono)' }}>
                        #{p.currentRobotSerial}
                      </span>
                    ) : (
                      '–'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// 로더 페이지 공통 컨테이너 — LoadersListPage/LoaderRevsPage 와 동일(전폭·좌측
// 정렬, 여백은 AppShell main 이 제공). 페이지 간 정렬 불일치 제거.
const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};
const backLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  color: 'var(--fg-muted)',
  marginBottom: 8,
};
const titleStyle: CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  margin: 0,
  color: 'var(--fg-default)',
};
const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const sectionHeaderStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  margin: 0,
  color: 'var(--fg-default)',
};
const tableWrap: CSSProperties = {
  overflowX: 'auto',
};
const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
const thBase: CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};
const thLeft: CSSProperties = { ...thBase, textAlign: 'left' };
const thRight: CSSProperties = { ...thBase, textAlign: 'right' };
const tdBase: CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-subtle)',
  color: 'var(--fg-default)',
  verticalAlign: 'middle',
};
const tdLeft: CSSProperties = { ...tdBase, textAlign: 'left' };
const tdNum: CSSProperties = {
  ...tdBase,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
const barRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};
const barTrack: CSSProperties = {
  flex: 1,
  height: 8,
  borderRadius: 999,
  background: 'var(--bg-sunken)',
  overflow: 'hidden',
  minWidth: 60,
};
const barFill: CSSProperties = {
  height: '100%',
  borderRadius: 999,
};
const pctText: CSSProperties = {
  minWidth: 36,
  textAlign: 'right',
  fontSize: 12,
  color: 'var(--fg-muted)',
};
const loadingStyle: CSSProperties = { padding: 40, textAlign: 'center', color: 'var(--fg-muted)' };
const errorStyle: CSSProperties = {
  padding: 16,
  color: 'var(--fg-danger)',
  background: 'var(--bg-danger-subtle)',
  borderRadius: 'var(--r-card)',
};
const emptyBlock: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--r-card)',
  fontSize: 13,
};
